import {
  type Collection,
  type ConversationId,
  type InferenceOptions,
  type InferenceSettings,
  type ToolCall,
  ToolName,
  type ToolResult,
} from "@superego/backend";
import { inferenceOptionsHas } from "@superego/shared-utils";
import { compact } from "es-toolkit";
import { DateTime } from "luxon";
import type InferenceService from "../../requirements/InferenceService.js";
import type JavascriptSandbox from "../../requirements/JavascriptSandbox.js";
import type TypescriptCompiler from "../../requirements/TypescriptCompiler.js";
import type CollectionsGetTypescriptSchema from "../../usecases/collections/GetTypescriptSchema.js";
import type DocumentsCreateMany from "../../usecases/documents/CreateMany.js";
import type DocumentsCreateNewVersion from "../../usecases/documents/CreateNewVersion.js";
import type DocumentsExecuteTypescriptFunction from "../../usecases/documents/ExecuteTypescriptFunction.js";
import type DocumentsList from "../../usecases/documents/List.js";
import type DocumentsSearch from "../../usecases/documents/Search.js";
import type FilesGetContent from "../../usecases/files/GetContent.js";
import Assistant from "../Assistant.js";
import InspectFile from "../shared-tools/InspectFile.js";
import Unknown from "../shared-tools/Unknown.js";
import defaultDeveloperPrompt from "./default-developer-prompt.md?raw";
import CreateChart from "./tools/CreateChart.js";
import CreateDocuments from "./tools/CreateDocuments.js";
import CreateDocumentsTables from "./tools/CreateDocumentsTables.js";
import CreateGeoJSONMap from "./tools/CreateGeoJSONMap.js";
import CreateNewDocumentVersion from "./tools/CreateNewDocumentVersion.js";
import ExecuteTypescriptFunction from "./tools/ExecuteTypescriptFunction.js";
import GetCollectionTypescriptSchema from "./tools/GetCollectionTypescriptSchema.js";
import SearchDocuments from "./tools/SearchDocuments.js";

export default class FactotumAssistant extends Assistant {
  constructor(
    private conversationId: ConversationId,
    private userInfo: string | null,
    private userPreferences: string | null,
    private developerPrompt: string | null,
    protected inferenceService: InferenceService,
    private collections: Collection[],
    private usecases: {
      documentsCreateMany: DocumentsCreateMany;
      documentsCreateNewVersion: DocumentsCreateNewVersion;
      documentsExecuteTypescriptFunction: DocumentsExecuteTypescriptFunction;
      documentsList: DocumentsList;
      documentsSearch: DocumentsSearch;
      collectionsGetTypescriptSchema: CollectionsGetTypescriptSchema;
      filesGetContent: FilesGetContent;
    },
    private javascriptSandbox: JavascriptSandbox,
    private typescriptCompiler: TypescriptCompiler,
    private savepoint: {
      create: () => Promise<string>;
      rollbackTo: (name: string) => Promise<void>;
    },
  ) {
    super();
  }

  static getDefaultDeveloperPrompt(): string {
    return defaultDeveloperPrompt;
  }

  protected getDeveloperPrompt(): string {
    return (this.developerPrompt ?? defaultDeveloperPrompt)
      .replaceAll("$TOOL_NAME_CREATE_DOCUMENTS", ToolName.CreateDocuments)
      .replaceAll(
        "$TOOL_NAME_CREATE_NEW_DOCUMENT_VERSION",
        ToolName.CreateNewDocumentVersion,
      )
      .replaceAll(
        "$TOOL_NAME_EXECUTE_TYPESCRIPT_FUNCTION",
        ToolName.ExecuteTypescriptFunction,
      )
      .replaceAll(
        "$TOOL_NAME_GET_COLLECTION_TYPESCRIPT_SCHEMA",
        ToolName.GetCollectionTypescriptSchema,
      )
      .replaceAll("$TOOL_NAME_CREATE_CHART", ToolName.CreateChart)
      .replaceAll("$TOOL_NAME_CREATE_GEO_JSON_MAP", ToolName.CreateGeoJSONMap)
      .replaceAll(
        "$TOOL_NAME_CREATE_DOCUMENTS_TABLE",
        ToolName.CreateDocumentsTables,
      )
      .replaceAll("$TOOL_NAME_SEARCH_DOCUMENTS", ToolName.SearchDocuments)
      .replaceAll("$TOOL_NAME_INSPECT_FILE", ToolName.InspectFile);
  }

  protected getUserContextPrompt(): string {
    const now = DateTime.now();
    return compact([
      "<collections>",
      JSON.stringify(
        this.collections.map((collection) => ({
          id: collection.id,
          name: collection.settings.name,
          description: collection.settings.description,
          assistantInstructions: collection.settings.assistantInstructions,
        })),
      ),
      "</collections>",
      "<local-date-time>",
      now.toISO({ precision: "millisecond", includeOffset: true }),
      "</local-date-time>",
      "<user-time-zone>",
      now.zone.name,
      "</user-time-zone>",
      "<weekday>",
      now.toFormat("cccc"),
      "</weekday>",
      this.userInfo ? "<user-info>" : null,
      this.userInfo,
      this.userInfo ? "</user-info>" : null,
      this.userPreferences ? "<user-preferences>" : null,
      this.userPreferences,
      this.userPreferences ? "</user-preferences>" : null,
    ]).join("\n");
  }

  protected getTools(
    inferenceSettings: InferenceSettings,
    inferenceOptions: InferenceOptions<"completion">,
  ): InferenceService.Tool[] {
    return compact([
      GetCollectionTypescriptSchema.get(),
      ExecuteTypescriptFunction.get(),
      CreateDocuments.get(),
      CreateNewDocumentVersion.get(),
      CreateChart.get(),
      CreateGeoJSONMap.get(),
      CreateDocumentsTables.get(),
      SearchDocuments.get(),
      inferenceOptionsHas(inferenceOptions, "fileInspection")
        ? InspectFile.get(inferenceSettings, inferenceOptions)
        : null,
    ]);
  }

  protected async processToolCall(
    toolCall: ToolCall,
    inferenceOptions: InferenceOptions<"completion">,
  ): Promise<ToolResult> {
    if (GetCollectionTypescriptSchema.is(toolCall)) {
      return GetCollectionTypescriptSchema.exec(
        toolCall,
        this.usecases.collectionsGetTypescriptSchema,
      );
    }
    if (ExecuteTypescriptFunction.is(toolCall)) {
      return ExecuteTypescriptFunction.exec(
        toolCall,
        this.usecases.documentsExecuteTypescriptFunction,
      );
    }
    if (CreateDocuments.is(toolCall)) {
      return CreateDocuments.exec(
        toolCall,
        this.conversationId,
        this.collections,
        this.usecases.documentsCreateMany,
        this.savepoint,
      );
    }
    if (CreateNewDocumentVersion.is(toolCall)) {
      return CreateNewDocumentVersion.exec(
        toolCall,
        this.conversationId,
        this.collections,
        this.usecases.documentsCreateNewVersion,
      );
    }
    if (CreateChart.is(toolCall)) {
      return CreateChart.exec(
        toolCall,
        this.collections,
        this.usecases.documentsList,
        this.javascriptSandbox,
        this.typescriptCompiler,
      );
    }
    if (CreateGeoJSONMap.is(toolCall)) {
      return CreateGeoJSONMap.exec(
        toolCall,
        this.collections,
        this.usecases.documentsList,
        this.javascriptSandbox,
        this.typescriptCompiler,
      );
    }
    if (CreateDocumentsTables.is(toolCall)) {
      return CreateDocumentsTables.exec(
        toolCall,
        this.collections,
        this.usecases.documentsList,
        this.javascriptSandbox,
        this.typescriptCompiler,
      );
    }
    if (SearchDocuments.is(toolCall)) {
      return SearchDocuments.exec(toolCall, this.usecases.documentsSearch);
    }
    if (
      InspectFile.is(toolCall) &&
      inferenceOptionsHas(inferenceOptions, "fileInspection")
    ) {
      return InspectFile.exec(
        toolCall,
        this.inferenceService,
        this.usecases.filesGetContent,
        inferenceOptions,
      );
    }
    return Unknown.exec(toolCall);
  }
}
